import { useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TopNav } from './TopNav';
import { MobileBottomNav } from './MobileBottomNav';
import { PageContainer } from './PageContainer';
import {
  useKeyboardShortcuts,
  ShortcutConfig,
} from '@/hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from '@/components/shared/KeyboardShortcutsHelp';

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const hideMobileBottomNav = location.pathname.startsWith('/talk/');
  const fullWidthLayout = location.pathname.startsWith('/projects/workflow/');

  const shortcuts = useMemo<ShortcutConfig[]>(
    () => [
      {
        key: 'd',
        chord: 'g',
        action: () => navigate('/'),
        description: 'Go to Dashboard',
        category: 'Navigation',
      },
      {
        key: 'p',
        chord: 'g',
        action: () => navigate('/projects'),
        description: 'Go to Projects',
        category: 'Navigation',
      },
      {
        key: 't',
        chord: 'g',
        action: () => navigate('/talk'),
        description: 'Go to Talk',
        category: 'Navigation',
      },
      {
        key: 's',
        chord: 'g',
        action: () => navigate('/sessions'),
        description: 'Go to Sessions',
        category: 'Navigation',
      },
      {
        key: 'b',
        chord: 'g',
        action: () => navigate('/databases'),
        description: 'Go to Databases',
        category: 'Navigation',
      },
      {
        key: 'r',
        chord: 'g',
        action: () => navigate('/registry'),
        description: 'Go to Registry',
        category: 'Navigation',
      },
      {
        key: 'n',
        action: () => navigate('/projects?action=new'),
        description: 'New Project',
        category: 'Actions',
      },
      {
        key: '?',
        action: () => setHelpOpen(true),
        description: 'Show keyboard shortcuts',
        category: 'General',
      },
    ],
    [navigate],
  );

  useKeyboardShortcuts(shortcuts);

  const displayShortcuts = useMemo(
    () =>
      shortcuts.map((s) => ({
        keys: s.chord
          ? [s.chord.toUpperCase(), s.key.toUpperCase()]
          : [s.key === '?' ? '?' : s.key.toUpperCase()],
        description: s.description,
        category: s.category,
      })),
    [shortcuts],
  );

  return (
    <div
      className={
        fullWidthLayout
          ? 'flex h-dvh flex-col overflow-hidden bg-background'
          : 'flex min-h-screen flex-col bg-background'
      }
    >
      <TopNav />
      {fullWidthLayout ? (
        <main className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          <Outlet />
        </main>
      ) : (
        <div className="flex-1">
          <PageContainer>
            <Outlet />
          </PageContainer>
        </div>
      )}
      {!hideMobileBottomNav ? <MobileBottomNav /> : null}
      <KeyboardShortcutsHelp
        open={helpOpen}
        onOpenChange={setHelpOpen}
        shortcuts={displayShortcuts}
      />
    </div>
  );
}
